import { lazy, Suspense, type ComponentType } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
  Route,
  Navigate,
  Outlet,
} from 'react-router';
import { PageSkeleton } from './components/ui/DataLoader';
import { fundsCarryNavItems } from './config/entity-navigation';
import { AppProviders } from './components/providers/AppProviders';
import { PortalProviders } from './portal/shared/providers/PortalProviders';
import Layout from './components/layout/Layout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/auth/LoginPage';
import VerifyPage from './pages/auth/VerifyPage';
import GoogleSuccessPage from './pages/auth/GoogleSuccessPage';
import GoogleErrorPage from './pages/auth/GoogleErrorPage';
import PontoCallbackPage from './pages/auth/PontoCallbackPage';
import AgentConsentPage from './pages/auth/AgentConsentPage';
import DashboardPage from './pages/DashboardPage';
import InvestorsPage from './pages/investor/InvestorsPage';
import InvestorFormPage from './pages/investor/InvestorFormPage';
import InvestorContactDetailPage from './pages/investor/InvestorContactDetailPage';
import CapitalCallsPage from './pages/CapitalCallsPage';
import FundsPage from './pages/funds/FundsPage';
import FundFormPage from './pages/funds/FundFormPage';
import { FundPageLayout } from './components/fund-detail';
import { fundCostsPath } from './components/fund-costs/fundCostFormState';
import {
  FundTransactionParentBreadcrumb,
  FundTransactionViewPage,
} from './pages/funds/transactions/FundTransactionViewPage';
import DrawdownPreviewPage from './pages/drawdowns/DrawdownPreviewPage';
import DrawdownCreatePage from './pages/drawdowns/DrawdownCreatePage';
import DrawdownMatrixPage from './pages/drawdowns/DrawdownMatrixPage';
import UserManagementPage from './pages/UserManagementPage';
import { PortfolioPageLayout } from './components/portfolio/PortfolioPageLayout';

// Admin pages
import { AdminLayout } from './components/admin/AdminLayout';
import { UserManagement } from './components/admin/UserManagement';
import { PortalRolesPermissions } from './components/admin/PortalRolesPermissions';
import { FundManagement } from './components/admin/FundManagement';
import DocumentTagsPage from './pages/admin/DocumentTagsPage';
import TemplatesPage from './pages/admin/TemplatesPage';
import DocumentTemplateFormPage from './pages/admin/DocumentTemplateFormPage';
import EmailTemplateFormPage from './pages/admin/EmailTemplateFormPage';

// Investor Portal
import { portalRoutes } from './portal/router';

// --- Lazy-loaded heavy pages ---

function lazyNamed<K extends string, T extends Record<K, ComponentType>>(
  load: () => Promise<T>,
  name: K
) {
  return lazy(() => load().then((m) => ({ default: m[name] })));
}

// Portfolio
const PortfolioPage = lazy(() => import('./pages/portfolio/PortfolioPage'));
const PortfolioCompanyDetailPage = lazy(
  () => import('./pages/portfolio/PortfolioCompanyDetailPage')
);
const PortfolioCompanyFormPage = lazy(
  () => import('./pages/portfolio/PortfolioCompanyFormPage')
);
const PortfolioCompanyNewPage = lazy(
  () => import('./pages/portfolio/PortfolioCompanyNewPage')
);
const TransactionMatrixPage = lazy(
  () => import('./pages/portfolio/TransactionMatrixPage')
);
const TransactionTypePicker = lazyNamed(
  () => import('./components/portfolio/transactions/TransactionTypePicker'),
  'TransactionTypePicker'
);
const CreateTransactionPage = lazyNamed(
  () => import('./components/portfolio/transactions/CreateTransactionPage'),
  'CreateTransactionPage'
);
const MetricsImportReviewPage = lazyNamed(
  () => import('./components/portfolio/metrics-import/MetricsImportReviewPage'),
  'MetricsImportReviewPage'
);
const TransactionViewPage = lazyNamed(
  () => import('./components/portfolio/transactions/TransactionViewPage'),
  'TransactionViewPage'
);
const TransactionEditPage = lazyNamed(
  () => import('./components/portfolio/transactions/TransactionEditPage'),
  'TransactionEditPage'
);
const TransactionApprovePage = lazyNamed(
  () => import('./components/portfolio/transactions/TransactionApprovePage'),
  'TransactionApprovePage'
);
const AssetTransactionViewPage = lazyNamed(
  () => import('./components/portfolio/transactions/AssetTransactionViewPage'),
  'AssetTransactionViewPage'
);
const AssetSaleEditPage = lazyNamed(
  () => import('./components/portfolio/transactions/AssetSaleEditPage'),
  'AssetSaleEditPage'
);
const ConvertibleDetailPage = lazyNamed(
  () => import('./components/cap-table/ConvertibleDetailPage'),
  'ConvertibleDetailPage'
);
const EditFinancingRoundPage = lazyNamed(
  () => import('./components/portfolio/transactions/EditFinancingRoundPage'),
  'EditFinancingRoundPage'
);

// Fund detail
const FundDetailPage = lazy(() => import('./pages/funds/FundDetailPage'));
const MetricsReleaseQueuePage = lazy(
  () => import('./pages/funds/MetricsReleaseQueuePage')
);
const FundEmailPage = lazy(() => import('./pages/funds/FundEmailPage'));
const FundSetupPage = lazy(() => import('./pages/funds/FundSetupPage'));

// Distributions
const DistributionFormPage = lazy(
  () => import('./pages/distributions/DistributionFormPage')
);
const loadDistributionDetailPage = () =>
  import('./pages/distributions/DistributionDetailPage');
const DistributionDetailPage = lazy(loadDistributionDetailPage);
const FundReportCreatePage = lazy(
  () => import('./pages/funds/FundReportCreatePage')
);
const FundCostCreatePage = lazy(
  () => import('./pages/funds/fund-costs/FundCostCreatePage')
);
const FundCostEditPage = lazy(
  () => import('./pages/funds/fund-costs/FundCostEditPage')
);
const FundCostDesignatePage = lazy(
  () => import('./pages/funds/fund-costs/FundCostDesignatePage')
);
const FundReportTemplatePage = lazy(
  () => import('./pages/funds/FundReportTemplatePage')
);
const loadFundReportPage = () => import('./pages/funds/FundReportPage');
const FundReportPage = lazy(loadFundReportPage);
const FundReportBreadcrumb = lazyNamed(
  loadFundReportPage,
  'FundReportBreadcrumb'
);
const DistributionBreadcrumb = lazyNamed(
  loadDistributionDetailPage,
  'DistributionBreadcrumb'
);

// Scenarios
const ScenarioComparisonPage = lazy(
  () => import('./pages/ScenarioComparisonPage')
);

// Reports & Documents
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const DocumentsPage = lazy(() => import('./pages/DocumentsPage'));

// Funds carry
const FundMembersPage = lazy(
  () => import('./pages/funds-carry/members/FundMembersPage')
);
const FundMemberNewPage = lazy(
  () => import('./pages/funds-carry/members/FundMemberNewPage')
);
const FundMemberDetailPage = lazy(
  () => import('./pages/funds-carry/members/FundMemberDetailPage')
);
const CarryDocumentsPage = lazy(
  () => import('./pages/funds-carry/documents/CarryDocumentsPage')
);
const CarryFundDocumentsPage = lazy(
  () => import('./pages/funds-carry/documents/CarryFundDocumentsPage')
);
const CarryDocumentUploadPage = lazy(
  () => import('./pages/funds-carry/documents/CarryDocumentUploadPage')
);
const CarryDocumentViewPage = lazy(
  () => import('./pages/funds-carry/documents/CarryDocumentViewPage')
);
const CarryDocumentEditPage = lazy(
  () => import('./pages/funds-carry/documents/CarryDocumentEditPage')
);
const CarryFundsPage = lazy(
  () => import('./pages/funds-carry/funds/CarryFundsPage')
);
const CarryAllocatePage = lazy(
  () => import('./pages/funds-carry/funds/CarryAllocatePage')
);
const CarryFundEditPage = lazy(
  () => import('./pages/funds-carry/funds/CarryFundEditPage')
);
const CarryFundDetailPage = lazy(
  () => import('./pages/funds-carry/funds/CarryFundDetailPage')
);
const CarryStructurePage = lazy(
  () => import('./pages/funds-carry/structure/CarryStructurePage')
);
const CarryStakPage = lazy(
  () => import('./pages/funds-carry/structure/CarryStakPage')
);
const CarryCooperativePage = lazy(
  () => import('./pages/funds-carry/structure/CarryCooperativePage')
);
const CarryHoldingPage = lazy(
  () => import('./pages/funds-carry/structure/CarryHoldingPage')
);
const CarryStructureFundPage = lazy(
  () => import('./pages/funds-carry/structure/CarryStructureFundPage')
);
const FundsCarryPlaceholderPage = lazy(
  () => import('./pages/funds-carry/FundsCarryPlaceholderPage')
);

// Admin pages (FxRates pulls in recharts)
const FxRatesPage = lazy(() => import('./pages/admin/FxRatesPage'));
const FinancialMetricsPage = lazy(
  () => import('./pages/admin/FinancialMetricsPage')
);
const ChartOfAccountsPage = lazy(
  () => import('./pages/admin/ChartOfAccountsPage')
);
const BankCodesPage = lazy(() => import('./pages/admin/BankCodesPage'));
const PontoBankSyncPage = lazy(() => import('./pages/admin/PontoBankSyncPage'));
const InvestorExportsPage = lazy(
  () => import('./pages/admin/InvestorExportsPage')
);
const BankIncomingPage = lazy(() => import('./pages/admin/BankIncomingPage'));
const JobSchedulerPage = lazy(() => import('./pages/admin/JobSchedulerPage'));
const EmailQueuePage = lazy(() => import('./pages/EmailQueuePage'));
const NewsSourcesPage = lazyNamed(
  () => import('./pages/news/NewsSourcesPage'),
  'NewsSourcesPage'
);
const SystemSettings = lazy(
  () => import('./components/admin/scheduler/SystemSettings')
);

function AppRoot() {
  return (
    <AppProviders>
      <Outlet />
    </AppProviders>
  );
}

function PortalRoot() {
  return (
    <PortalProviders>
      <Outlet />
    </PortalProviders>
  );
}

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route
      element={
        <div className="min-h-screen bg-background font-sans antialiased">
          <Outlet />
        </div>
      }
    >
      {/* ========================================
          INVESTOR PORTAL (without AuthProvider)
          ======================================== */}
      <Route path="/portal" element={<PortalRoot />}>
        {portalRoutes}
      </Route>

      {/* Carry lives inside the investor portal at /portal/carry */}
      <Route path="/carry" element={<Navigate to="/portal/login" replace />} />

      {/* ========================================
          ADMIN APPLICATION (with AuthProvider)
          ======================================== */}
      <Route element={<AppRoot />}>
        {/* Public routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/auth/login" element={<LoginPage />} />
        <Route path="/auth/verify" element={<VerifyPage />} />
        <Route path="/auth/success" element={<GoogleSuccessPage />} />
        <Route path="/auth/error" element={<GoogleErrorPage />} />
        <Route path="/auth/callback" element={<PontoCallbackPage />} />

        {/* Agent (MCP) OAuth consent - focused page, no app chrome */}
        <Route
          path="/oauth/consent"
          element={
            <ProtectedRoute>
              <AgentConsentPage />
            </ProtectedRoute>
          }
        />

        {/* Protected routes with Layout */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Layout>
                <DashboardPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/app"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="funds" element={<FundsPage />} />
          <Route
            path="funds/metrics-release"
            element={<MetricsReleaseQueuePage />}
          />
          <Route path="funds/new" element={<FundFormPage />} />
          <Route
            path="funds/:fundId/drawdowns/new"
            element={<DrawdownCreatePage />}
          />
          <Route
            path="funds/:fundId/drawdowns/matrix"
            element={<DrawdownMatrixPage />}
          />
          <Route
            path="funds/:fundId/drawdowns/:drawdownId/preview"
            element={<DrawdownPreviewPage />}
          />
          <Route path="funds/:fundId" element={<FundPageLayout />}>
            <Route index element={<FundDetailPage />} />
            <Route
              path="fund-transactions/:transactionId"
              element={<FundTransactionViewPage />}
              handle={{
                breadcrumbs: () => [
                  { label: <FundTransactionParentBreadcrumb /> },
                  { label: 'Transaction' },
                ],
              }}
            />
            <Route
              path="edit"
              element={<FundFormPage />}
              handle={{ breadcrumbs: [{ label: 'Edit' }] }}
            />
            <Route
              path="fund-costs/new"
              element={<FundCostCreatePage />}
              handle={{
                breadcrumbs: ({
                  params,
                }: {
                  params: Record<string, string>;
                }) => [
                  { label: 'Fund Costs', href: fundCostsPath(params.fundId) },
                  { label: 'New Fund Cost' },
                ],
              }}
            />
            <Route
              path="fund-costs/:costId/edit"
              element={<FundCostEditPage />}
              handle={{
                breadcrumbs: ({
                  params,
                }: {
                  params: Record<string, string>;
                }) => [
                  { label: 'Fund Costs', href: fundCostsPath(params.fundId) },
                  { label: 'Edit Fund Cost' },
                ],
              }}
            />
            <Route
              path="fund-costs/designate/:incomingId"
              element={<FundCostDesignatePage />}
              handle={{
                breadcrumbs: ({
                  params,
                }: {
                  params: Record<string, string>;
                }) => [
                  { label: 'Fund Costs', href: fundCostsPath(params.fundId) },
                  { label: 'Designate Bank Line' },
                ],
              }}
            />
            <Route
              path="reports/new"
              element={<FundReportCreatePage />}
              handle={{
                breadcrumbs: ({
                  params,
                }: {
                  params: Record<string, string>;
                }) => [
                  {
                    label: 'Reports',
                    href: `/app/funds/${params.fundId}?tab=reports`,
                  },
                  { label: 'New Report' },
                ],
              }}
            />
            <Route
              path="reports/templates/:templateId"
              element={<FundReportTemplatePage />}
              handle={{
                breadcrumbs: ({
                  params,
                }: {
                  params: Record<string, string>;
                }) => [
                  {
                    label: 'Reports',
                    href: `/app/funds/${params.fundId}?tab=reports`,
                  },
                  { label: 'Template' },
                ],
              }}
            />
            <Route
              path="reports/:reportId"
              element={<FundReportPage />}
              handle={{
                breadcrumbs: ({
                  params,
                }: {
                  params: Record<string, string>;
                }) => [
                  {
                    label: 'Reports',
                    href: `/app/funds/${params.fundId}?tab=reports`,
                  },
                  { label: <FundReportBreadcrumb /> },
                ],
              }}
            />
            <Route
              path="distributions/new"
              element={<DistributionFormPage />}
              handle={{
                breadcrumbs: ({
                  params,
                }: {
                  params: Record<string, string>;
                }) => [
                  {
                    label: 'Distributions',
                    href: `/app/funds/${params.fundId}?tab=transactions&section=distributions`,
                  },
                  { label: 'New Distribution' },
                ],
              }}
            />
            <Route
              path="distributions/:distributionId"
              element={<DistributionDetailPage />}
              handle={{
                breadcrumbs: ({
                  params,
                }: {
                  params: Record<string, string>;
                }) => [
                  {
                    label: 'Distributions',
                    href: `/app/funds/${params.fundId}?tab=transactions&section=distributions`,
                  },
                  { label: <DistributionBreadcrumb /> },
                ],
              }}
            />
            <Route
              path="email-compose"
              element={<FundEmailPage />}
              handle={{
                breadcrumbs: ({
                  params,
                }: {
                  params: Record<string, string>;
                }) => [
                  {
                    label: 'Fund Emails',
                    href: `/app/funds/${params.fundId}?tab=emails`,
                  },
                  { label: 'New Emails' },
                ],
              }}
            />
          </Route>
          <Route path="investors" element={<InvestorsPage />} />
          <Route path="investors/new" element={<InvestorFormPage />} />
          <Route path="investors/:investorId" element={<InvestorFormPage />} />
          <Route
            path="investors/:investorId/edit"
            element={<InvestorFormPage />}
          />
          <Route
            path="investors/:investorId/contacts/new"
            element={<InvestorContactDetailPage />}
          />
          <Route
            path="investors/:investorId/contacts/:contactId"
            element={<InvestorContactDetailPage />}
          />
          <Route path="capital-calls" element={<CapitalCallsPage />} />
          <Route path="funds-carry">
            <Route index element={<Navigate to="funds" replace />} />
            <Route path="members" element={<FundMembersPage />} />
            <Route path="members/new" element={<FundMemberNewPage />} />
            <Route
              path="members/:fundMemberId"
              element={<FundMemberDetailPage />}
            />
            <Route path="documents" element={<CarryDocumentsPage />} />
            <Route
              path="documents/:fundId"
              element={<CarryFundDocumentsPage />}
            />
            <Route
              path="documents/:fundId/upload"
              element={<CarryDocumentUploadPage />}
            />
            <Route
              path="documents/:fundId/:carryDocumentId"
              element={<CarryDocumentViewPage />}
            />
            <Route
              path="documents/:fundId/:carryDocumentId/edit"
              element={<CarryDocumentEditPage />}
            />
            <Route path="funds" element={<CarryFundsPage />} />
            <Route path="funds/:fundId" element={<CarryFundDetailPage />} />
            <Route path="funds/:fundId/edit" element={<CarryFundEditPage />} />
            <Route
              path="funds/:fundId/allocate"
              element={<CarryAllocatePage />}
            />
            <Route path="structure">
              <Route index element={<CarryStructurePage />} />
              <Route path="stak" element={<CarryStakPage />} />
              <Route path="cooperative" element={<CarryCooperativePage />} />
              <Route
                path="holdings/:fundMemberId"
                element={<CarryHoldingPage />}
              />
              <Route
                path="funds/:fundId"
                element={<CarryStructureFundPage />}
              />
            </Route>
            {fundsCarryNavItems
              .filter(
                (item) =>
                  !['funds', 'members', 'documents', 'structure'].includes(
                    item.value
                  )
              )
              .map((item) => (
                <Route
                  key={item.value}
                  path={item.value}
                  element={<FundsCarryPlaceholderPage title={item.label} />}
                />
              ))}
          </Route>
          <Route path="portfolio" element={<PortfolioPage />} />
          <Route path="portfolio/new" element={<PortfolioCompanyNewPage />} />
          <Route
            path="portfolio/:companyId"
            element={<PortfolioCompanyDetailPage />}
          />
          <Route path="portfolio/:companyId" element={<PortfolioPageLayout />}>
            <Route path="edit" element={<PortfolioCompanyFormPage />} />
            <Route
              path="metrics/import"
              element={<MetricsImportReviewPage />}
            />
            <Route
              path="transactions/new"
              element={<TransactionTypePicker />}
            />
            <Route
              path="transactions/new/:type"
              element={<CreateTransactionPage />}
            />
            <Route
              path="transactions/:transactionId"
              element={<TransactionViewPage />}
            />
            <Route
              path="transactions/:transactionId/edit"
              element={<TransactionEditPage />}
            />
            <Route
              path="convertibles/:convertibleId"
              element={<ConvertibleDetailPage />}
            />
            <Route
              path="rounds/:roundId/edit"
              element={<EditFinancingRoundPage />}
            />
            <Route
              path="asset-transactions/:assetTransactionId"
              element={<AssetTransactionViewPage />}
            />
            <Route
              path="asset-transactions/:assetTransactionId/edit"
              element={<AssetSaleEditPage />}
            />
            <Route
              path="asset-transactions/:assetTransactionId/approve"
              element={<TransactionApprovePage />}
            />
          </Route>
          <Route path="reports" element={<ReportsPage />} />
          <Route path="documents" element={<DocumentsPage />} />
          <Route path="fund-setup/:fundId?" element={<FundSetupPage />} />
          <Route
            path="users"
            element={
              <ProtectedRoute
                requiredRoles={['super_admin', 'general_partner']}
              >
                <UserManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin"
            element={
              <ProtectedRoute
                requiredRoles={['super_admin', 'general_partner']}
              >
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="users" replace />} />
            <Route path="users" element={<UserManagement />} />
            <Route path="portal-roles" element={<PortalRolesPermissions />} />
            <Route path="funds" element={<FundManagement />} />
            <Route path="chart-of-accounts" element={<ChartOfAccountsPage />} />
            <Route path="bank-codes" element={<BankCodesPage />} />
            <Route path="fx-rates" element={<FxRatesPage />} />
            <Route path="ponto" element={<PontoBankSyncPage />} />
            <Route path="bank-incoming" element={<BankIncomingPage />} />
            <Route path="tags" element={<DocumentTagsPage />} />
            <Route path="templates" element={<TemplatesPage />} />
            <Route
              path="templates/document/new"
              element={<DocumentTemplateFormPage />}
              handle={{
                breadcrumbs: [
                  { label: 'Templates', href: '/app/admin/templates' },
                  { label: 'New Document Template' },
                ],
              }}
            />
            <Route
              path="templates/document/:templateId"
              element={<DocumentTemplateFormPage />}
              handle={{
                breadcrumbs: [
                  { label: 'Templates', href: '/app/admin/templates' },
                  { label: 'Edit Document Template' },
                ],
              }}
            />
            <Route
              path="templates/email/new"
              element={<EmailTemplateFormPage />}
              handle={{
                breadcrumbs: [
                  { label: 'Templates', href: '/app/admin/templates' },
                  { label: 'New Email Template' },
                ],
              }}
            />
            <Route
              path="templates/email/:templateId"
              element={<EmailTemplateFormPage />}
              handle={{
                breadcrumbs: [
                  { label: 'Templates', href: '/app/admin/templates' },
                  { label: 'Edit Email Template' },
                ],
              }}
            />
            <Route
              path="financial-metrics"
              element={<FinancialMetricsPage />}
            />
            <Route path="scheduler" element={<JobSchedulerPage />} />
            <Route path="news" element={<NewsSourcesPage />} />
            <Route path="email-queue" element={<EmailQueuePage />} />
            <Route path="investor-exports" element={<InvestorExportsPage />} />
            <Route path="settings" element={<SystemSettings />} />
          </Route>
        </Route>

        {/* Standalone pages without Layout - need their own Suspense (Layout normally provides it) */}
        <Route
          path="/app/portfolio/:companyId/matrix"
          element={
            <ProtectedRoute>
              <Suspense fallback={<PageSkeleton />}>
                <TransactionMatrixPage />
              </Suspense>
            </ProtectedRoute>
          }
        />

        <Route
          path="/app/scenarios/compare"
          element={
            <ProtectedRoute>
              <Suspense fallback={<PageSkeleton />}>
                <ScenarioComparisonPage />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Catch all - redirect to dashboard */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Route>
  )
);

function App() {
  return <RouterProvider router={router} />;
}

export default App;
